import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Hls from "hls.js";
import { imageUrlCheck, getThumbnailRatioConfig, stripHtml } from "../../utils/utils";
import { getPlayerToken } from "../../network/service";
import { getShowDetails } from "../../Screens/ShowDetailsScreen/service";
import { getPodcastModal } from "../../Redux/PodcastModal/podcastModalSlice";
import { getSelectedChannel } from "../../Redux/SelectedChannel/selectedChannelSlice";
import { getSelectedSchedule } from "../../Redux/SelectedScheduleChannel/selectedScheduleChannelSlice";
import Translate, { useTranslateText } from "../Multilanguage/Translate";
import Util from "./util";
import { setAiInfo } from "../../Redux/AiType/AiTypeSlice";
import { ReactComponent as Closeicon } from "../../assets/Icons/cross.svg";
import RemoveFromContinueWatchModal from "../Modals/RemoveFromContinueWatchModal";
import ReactDOM from "react-dom";
import "./scss/_yowCard.scss";

// Lightweight HLS/MP4 video player for YowCard hover preview popover
const CardTrailerVideoPlayer = ({ src, isMuted = true, onError, onVideoClick }) => {
  const vRef = useRef(null);
  const hlsRef = useRef(null);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    const video = vRef.current;
    if (!video) return;
    video.muted = isMuted;
    video.defaultMuted = isMuted;
    if (!isMuted && video.paused) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((e) => {
          if (e.name !== "AbortError") {
            console.log("Unmute play error:", e);
          }
        });
      }
    }
  }, [isMuted]);

  useEffect(() => {
    const video = vRef.current;
    if (!video || !src) return;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    video.muted = isMuted;
    video.defaultMuted = isMuted;

    const isHls = typeof src === "string" && (src.includes(".m3u8") || src.includes("playlistV2") || src.includes("/m3u8"));

    if (isHls && Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: false,
        lowLatencyMode: false,
        backBufferLength: 15,
      });

      hlsRef.current = hls;

      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src);
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (video) {
          video.muted = isMuted;
          video.defaultMuted = isMuted;
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.catch((e) => {
              if (e.name !== "AbortError") {
                console.log("Card HLS play error:", e);
                video.muted = true;
                video.play().catch(() => { });
              }
            });
          }
        }
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.warn("HLS fatal error in CardTrailerVideoPlayer:", data.type, data.details);
          if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
          }
          if (video && video.canPlayType && video.canPlayType("application/vnd.apple.mpegurl")) {
            video.src = src;
            video.muted = true;
            video.defaultMuted = true;
            video.load();
            video.play().catch(() => { });
          } else if (onErrorRef.current) {
            onErrorRef.current(data);
          }
        }
      });

      hls.attachMedia(video);
    } else if (isHls && video.canPlayType && video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
      video.muted = true;
      video.defaultMuted = true;
      video.load();
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((e) => {
          if (e.name !== "AbortError") {
            console.log("Native HLS play error:", e);
            video.muted = true;
            video.play().catch(() => { });
            if (onErrorRef.current) onErrorRef.current(e);
          }
        });
      }
    } else {
      video.src = src;
      video.muted = true;
      video.defaultMuted = true;
      video.load();
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((e) => {
          if (e.name !== "AbortError") {
            console.log("Card MP4/direct video play error:", e);
            if (onErrorRef.current) onErrorRef.current(e);
          }
        });
      }
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src]);

  return (
    <video
      ref={vRef}
      className="modalTrailerVideo"
      autoPlay
      muted={isMuted}
      playsInline
      webkit-playsinline="true"
      referrerPolicy="no-referrer"
      preload="auto"
      loop
      onClick={onVideoClick}
      style={{ cursor: "pointer", pointerEvents: "auto" }}
      onError={(e) => {
        if (hlsRef.current) {
          return;
        }
        if (onErrorRef.current) onErrorRef.current(e);
      }}
    />
  );
};

const isTeaserUrl = (val) => {
  if (!val) return false;
  const s = String(val).trim();
  if (!s || s === "null" || s === "undefined") return false;
  return (
    s.startsWith("http://") ||
    s.startsWith("https://") ||
    s.startsWith("//") ||
    s.includes("http://") ||
    s.includes("https://") ||
    s.includes(".m3u8") ||
    s.includes(".mp4")
  );
};

const toNumericVideoId = (val) => {
  if (val === null || val === undefined) return null;
  const s = String(val).trim();
  if (/^\d+$/.test(s) && Number(s) > 0) {
    return s;
  }
  return null;
};

const extractTeaserVideoId = (item) => {
  if (!item) return null;

  // 1. video_time numeric ID
  const vtId = toNumericVideoId(item?.video_time);
  if (vtId) return vtId;

  // 2. teaser numeric ID or extracted from teaser URL path
  if (item?.teaser) {
    const tId = toNumericVideoId(item.teaser);
    if (tId) return tId;
    if (typeof item.teaser === "string") {
      const trimmed = item.teaser.trim();
      const match = trimmed.match(/\/(\d+)(?:\/[^/]*|\.m3u8)?$/);
      if (match && match[1] && Number(match[1]) > 0) {
        return match[1];
      }
      const parts = trimmed.split("/").filter(Boolean).reverse();
      for (const part of parts) {
        if (toNumericVideoId(part)) {
          return part;
        }
      }
    }
  }

  // 3. video_id
  const vId = toNumericVideoId(item?.video_id);
  if (vId) return vId;

  // 4. trailer
  const trId = toNumericVideoId(item?.trailer);
  if (trId) return trId;

  // 5. First video object in item.videos
  if (Array.isArray(item?.videos) && item.videos.length > 0) {
    const firstVid = item.videos[0];
    const fvTimeId = toNumericVideoId(firstVid?.video_time);
    if (fvTimeId) return fvTimeId;
    const fvId = toNumericVideoId(firstVid?.video_id);
    if (fvId) return fvId;
  }

  return null;
};

const teaserUrlCache = new Map();

// Teaser URL resolution helper: supports video_time (number or URL), teaser, trailer, and show details fallback
const resolveTeaserUrl = async (itemData, appInfoObj, pubidVal) => {
  if (!itemData) return null;

  const cacheKey = itemData?.show_id || itemData?.video_id || itemData?.video_time || itemData?.title;
  if (cacheKey && teaserUrlCache.has(cacheKey)) {
    return teaserUrlCache.get(cacheKey);
  }

  // 1. Direct URL check on itemData (video_time, teaser, trailer, videos)
  if (isTeaserUrl(itemData?.video_time)) {
    const directUrl = String(itemData.video_time).trim();
    if (cacheKey) teaserUrlCache.set(cacheKey, directUrl);
    return directUrl;
  }
  if (isTeaserUrl(itemData?.teaser)) {
    const directUrl = String(itemData.teaser).trim();
    if (cacheKey) teaserUrlCache.set(cacheKey, directUrl);
    return directUrl;
  }
  if (isTeaserUrl(itemData?.trailer)) {
    const directUrl = String(itemData.trailer).trim();
    if (cacheKey) teaserUrlCache.set(cacheKey, directUrl);
    return directUrl;
  }
  if (Array.isArray(itemData?.videos) && itemData.videos.length > 0) {
    const firstVid = itemData.videos[0];
    if (isTeaserUrl(firstVid?.video_time)) {
      const directUrl = String(firstVid.video_time).trim();
      if (cacheKey) teaserUrlCache.set(cacheKey, directUrl);
      return directUrl;
    }
  }

  // 2. Numeric video ID on itemData
  let videoId = extractTeaserVideoId(itemData);

  // 3. Fallback to fetching show details if no direct URL and no numeric videoId
  if (!videoId && itemData?.show_id) {
    try {
      const showRes = await getShowDetails(appInfoObj, itemData.show_id);
      const detail = showRes?.data?.data;
      if (detail) {
        if (isTeaserUrl(detail?.video_time)) {
          const directUrl = String(detail.video_time).trim();
          if (cacheKey) teaserUrlCache.set(cacheKey, directUrl);
          return directUrl;
        }
        if (isTeaserUrl(detail?.teaser)) {
          const directUrl = String(detail.teaser).trim();
          if (cacheKey) teaserUrlCache.set(cacheKey, directUrl);
          return directUrl;
        }
        if (isTeaserUrl(detail?.trailer)) {
          const directUrl = String(detail.trailer).trim();
          if (cacheKey) teaserUrlCache.set(cacheKey, directUrl);
          return directUrl;
        }
        if (Array.isArray(detail?.videos) && detail.videos.length > 0 && isTeaserUrl(detail.videos[0]?.video_time)) {
          const directUrl = String(detail.videos[0].video_time).trim();
          if (cacheKey) teaserUrlCache.set(cacheKey, directUrl);
          return directUrl;
        }
        videoId = extractTeaserVideoId(detail);
      }
    } catch (e) {
      console.error("Error fetching show details for teaser preview:", e);
    }
  }

  // 4. Fallback to explicit fallback_video_id if item provided one
  if (!videoId && itemData?.fallback_video_id) {
    videoId = toNumericVideoId(itemData.fallback_video_id);
  }

  // 5. Pubid 50187 global fallback so hover teaser video always plays smoothly
  const pId = pubidVal || appInfoObj?.projectDetails?.projectConfig?.pubid || appInfoObj?.projectDetails?.pubid || 50187;
  if (!videoId && String(pId) === "50187") {
    videoId = "186340";
  }

  // 6. Request player token for numeric videoId and build HLS playlist URL
  if (videoId) {
    try {
      const playerTokenResponse = await getPlayerToken(appInfoObj, videoId);
      if (playerTokenResponse?.status === 200 && playerTokenResponse?.data?.data) {
        const resolved = (
          process.env.REACT_APP_API_URL +
          "playlistV2/playlist.m3u8?id=" +
          videoId +
          "&token=" +
          playerTokenResponse?.data?.data +
          "&type=trailer_on_focus&pubid=" +
          pId
        );
        if (cacheKey) teaserUrlCache.set(cacheKey, resolved);
        return resolved;
      }
    } catch (err) {
      console.error("Error getting player token for card teaser:", err);
    }
  }

  return null;
};


const YowCard = ({
  data,
  metaData,
  imageUrl,
  type,
  thumbnailOrientation,
  playlist = true,
  showId,
  categoryName,
  tabIndex,
  role,
  ariaLabel,
  isChallenge,
  onRemove,
  displayedData,
  activeHoveredId,
  setActiveHoveredId,
  rankIndex,
  isPriority = false,
  disableHover = false,
  disableHoverPreview = false,
}) => {
  const {
    showHandleClick,
    replaceImage,
    addToWatchlist,
    removeFromWatchlist,
    subscriptionCheck,
  } = Util();
  const projectInfo = useSelector((state) => state?.projectInfo?.value);
  const accessToken = useSelector((state) => state?.accessToken?.value);
  const user = useSelector((state) => state?.user?.value);
  const userSubscriptionData = useSelector(
    (state) => state?.userSubscription?.value
  );
  const [watchlistStatus, setWatchlistStatus] = useState();
  const [imagURL, setImageURL] = useState(thumbnailOrientation === "PORTRAIT" ? require("../../assets/Images/loading-portrait.gif") : require("../../assets/Images/loading-landscape.gif"))
  const [isImageLoad, setIsImageLoad] = useState(false)
  const [isRemoveModal, setIsRemoveModal] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [continueWatchingList, setContinueWatchingList] = useState([]);
  const [hovered, setHovered] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const dispatch = useDispatch();
  const translateText = useTranslateText();
  const appInfo = {
    projectDetails: projectInfo,
    accessToken: accessToken,
  };
  const pubid = projectInfo?.projectConfig?.pubid || projectInfo?.pubid || 50187;
  const ai_type = data?.ai_type;
  useEffect(() => {
    if (data?.watchlist_flag === 1) {
      setWatchlistStatus("added");
    } else {
      setWatchlistStatus("removed");
    }
  }, [data]);

  useEffect(() => {
    setIsImageLoad(true);
    const rawImage =
      thumbnailOrientation === "PORTRAIT"
        ? data?.poster ||
          data?.poster_image ||
          data?.banner_portrait ||
          data?.portrait_image ||
          data?.portrait ||
          data?.thumbnail_portrait ||
          data?.image_portrait ||
          data?.poster_url ||
          data?.category_thumbnail ||
          data?.category_logo ||
          data?.logo ||
          data?.thumbnail ||
          data?.banner
        : data?.logo_thumb ||
          data?.banner ||
          data?.thumbnail_350_200 ||
          data?.logo ||
          data?.thumbnail ||
          data?.poster;

    let image;
    if (imageUrlCheck(rawImage)) {
      image = rawImage;
    } else if (data?.type === "SMART_HOME" || data?.type === "GENRE") {
      image = data?.banner ? data?.banner : (thumbnailOrientation === 'PORTRAIT' ? projectInfo?.projectConfig?.config?.PLACEHOLDER_IMAGE_PORTRAIT : projectInfo?.projectConfig?.config?.PLACEHOLDER_IMAGE);
    } else {
      image = process.env.REACT_APP_IMAGE_URL + (rawImage || "");
    }

    setImageURL(image);
  }, [data, thumbnailOrientation]);

  const [landscapeURL, setLandscapeURL] = useState("");

  useEffect(() => {
    let lImg = data?.logo_thumb || data?.banner || data?.thumbnail_350_200 || data?.logo || data?.thumbnail || data?.banner_portrait;
    if (lImg) {
      if (!imageUrlCheck(lImg)) {
        lImg = process.env.REACT_APP_IMAGE_URL + lImg;
      }
      setLandscapeURL(lImg);
    }
  }, [data]);

  useEffect(() => {
    if (data?.type === "CONTINUE_WATCHING" || type === "CONTINUE_WATCHING" || categoryName === "Continue Watching") {
      setContinueWatchingList(displayedData);
    }
  }, [data, type, categoryName, displayedData]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  const navigateToDetailsPage = (e) => {
    if (e) {
      if (typeof e.preventDefault === "function") e.preventDefault();
      if (typeof e.stopPropagation === "function") e.stopPropagation();
    }

    dispatch(setAiInfo(ai_type ?? null));

    if (data?.type === "LIVE_EVENT" || data?.type === "UPCOMING_EVENT" || data?.type === "EVENT") {
      navigate(`/event/${data?.vanity_url ? data?.vanity_url : data?.event_id}`, {
        state: { showId: data?.event_id },
      });
      return;
    }

    if (data?.type === "SMART_HOME") {
      navigate(`/genre/${data?.key || data?.vanity_url}`, { state: { key: data?.key } });
      return;
    }

    if (data?.type === "GENRE") {
      navigate(`/category/${data?.key || data?.vanity_url}`);
      return;
    }

    if (data?.type === "FASTCHANNEL" || data?.type === "FASTCHANNELS") {
      showHandleClick(
        data,
        type,
        projectInfo,
        thumbnailOrientation,
        getPodcastModal,
        getSelectedChannel,
        getSelectedSchedule,
        ai_type,
        showId,
        categoryName
      );
      return;
    }

    const targetVanity =
      data?.show_vanity_url ||
      data?.vanity_url ||
      data?.video_vanity_url ||
      data?.show_id ||
      data?.video_id ||
      showId;

    const targetShowId = data?.show_id || data?.video_id || showId;

    if (targetVanity) {
      navigate(`/show-details/${targetVanity}`, {
        state: {
          showId: targetShowId,
          type: data?.type,
          thumbnailOrientation,
          prevPath: location?.pathname,
        },
      });
    } else {
      showHandleClick(
        data,
        type,
        projectInfo,
        thumbnailOrientation,
        getPodcastModal,
        getSelectedChannel,
        getSelectedSchedule,
        ai_type,
        showId,
        categoryName
      );
    }
  };

  const handleCardClick = (e) => {
    dispatch(setAiInfo(ai_type ?? null));
    if (data?.type === "CONTINUE_WATCHING") {
      showHandleClick(
        data,
        type,
        projectInfo,
        thumbnailOrientation,
        getPodcastModal,
        getSelectedChannel,
        getSelectedSchedule,
        ai_type,
        showId,
        categoryName
      );
    } else {
      navigateToDetailsPage(e);
    }
  };

  const handleWatchlistClick = (e) => {
    if (e) e.stopPropagation();
    const targetId = data?.show_id || data?.video_id;
    const isVideo = !data?.show_id && Boolean(data?.video_id);
    if (targetId) {
      if (watchlistStatus === "added") {
        removeFromWatchlist(targetId, appInfo, (status) => setWatchlistStatus(status), isVideo);
      } else {
        addToWatchlist(user, targetId, appInfo, setWatchlistStatus, isVideo);
      }
    }
  };

  const handleCardKeyDown = (e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  const mainCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick(e);
    }
  };

  const cardRef = useRef(null);
  const isOverCardRef = useRef(false);
  const isOverModalRef = useRef(false);
  const closeTimerRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const teaserTimerRef = useRef(null);
  const cancelTeaserRef = useRef(false);
  const [modalPos, setModalPos] = useState(null);

  const [trailerUrl, setTrailerUrl] = useState(null);
  const [showTeaserVideo, setShowTeaserVideo] = useState(false);
  const [isTeaserMuted, setIsTeaserMuted] = useState(true);

  const handleTeaserError = useCallback(() => {
    setShowTeaserVideo(false);
  }, []);

  useEffect(() => {
    return () => {
      cancelTeaserRef.current = true;
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (teaserTimerRef.current) clearTimeout(teaserTimerRef.current);
    };
  }, []);

  useEffect(() => {
    setTrailerUrl(null);
    setShowTeaserVideo(false);
    if (isTeaserUrl(data?.video_time)) {
      setTrailerUrl(String(data.video_time).trim());
    } else if (isTeaserUrl(data?.teaser)) {
      setTrailerUrl(String(data.teaser).trim());
    }
  }, [data?.show_id, data?.video_id, data?.video_time, data?.teaser]);

  useEffect(() => {
    if (disableHover || disableHoverPreview) {
      cancelTeaserRef.current = true;
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (teaserTimerRef.current) clearTimeout(teaserTimerRef.current);
      setHovered(false);
      setShowTeaserVideo(false);
      setIsTeaserMuted(true);
    }
  }, [disableHover, disableHoverPreview]);

  const updateModalPosition = () => {
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight || rect.right < 0 || rect.left > window.innerWidth) {
        setHovered(false);
        return;
      }

      // Portrait thumbnails can extend up to gaiaCategoriesContainer / yowtv-categories;
      // Landscape thumbnails stay constrained within wrapper.
      const isPortrait = thumbnailOrientation === "PORTRAIT";
      const rowWrapper = isPortrait
        ? (
          cardRef.current.closest('.itemsContainer') ||
          cardRef.current.closest('.searchPage') ||
          cardRef.current.closest('.gaiaCategoriesContainer') ||
          cardRef.current.closest('.yowtv-categories') ||
          cardRef.current.closest('.top-trending-row') ||
          cardRef.current.closest('.categoryRow') ||
          cardRef.current.closest('.showsRowContainer') ||
          cardRef.current.closest('.commonPageContainer') ||
          cardRef.current.closest('.yow-carousel-container') ||
          cardRef.current.closest('.yowDetailsScreen') ||
          cardRef.current.closest('.wrapper') ||
          cardRef.current.closest('.swiper') ||
          cardRef.current.parentElement
        )
        : (
          cardRef.current.closest('.itemsContainer') ||
          cardRef.current.closest('.searchPage') ||
          cardRef.current.closest('.wrapper') ||
          cardRef.current.closest('.gaiaCategoriesContainer') ||
          cardRef.current.closest('.categoryRow') ||
          cardRef.current.closest('.yow-carousel-container') ||
          cardRef.current.closest('.yowDetailsScreen') ||
          cardRef.current.closest('.swiper') ||
          cardRef.current.closest('.showsRowContainer') ||
          cardRef.current.closest('.commonPageContainer') ||
          cardRef.current.parentElement
        );

      // Protect a navigation margin so expanding modals never cover Next/Prev carats
      const navSafetyMargin = 56;
      let rowLeft = isPortrait ? navSafetyMargin : 12;
      let rowRight = isPortrait ? window.innerWidth - navSafetyMargin : window.innerWidth - 12;

      if (rowWrapper) {
        const rowRect = rowWrapper.getBoundingClientRect();
        rowLeft = Math.max(isPortrait ? navSafetyMargin : 12, rowRect.left + (isPortrait ? 8 : 0));
        rowRight = Math.min(isPortrait ? window.innerWidth - navSafetyMargin : window.innerWidth - 12, rowRect.right - (isPortrait ? 8 : 0));
      }

      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const portraitConfig = getThumbnailRatioConfig(projectInfo, "PORTRAIT");
      const baseAspectNum = portraitConfig.numeric > 0 ? portraitConfig.numeric : (2 / 3);
      const baseRatioFactor = 436 / (232 * (baseAspectNum / (2 / 3)));

      const calculatedSize = Math.round(rect.width * baseRatioFactor);
      const availableWidth = rowRight - rowLeft;
      const viewTop = 16;
      const viewBottom = window.innerHeight - 16;
      const availableHeight = Math.max(200, viewBottom - viewTop);
      const maxAllowed = Math.min(436, availableWidth, availableHeight);
      const modalSize = Math.max(200, Math.min(calculatedSize, maxAllowed));

      // Centered on poster, expanding from center
      let left = centerX - modalSize / 2;
      let top = centerY - modalSize / 2;

      // Constrain modal horizontally within boundary
      if (left < rowLeft) {
        left = rowLeft;
      }
      if (left + modalSize > rowRight) {
        left = rowRight - modalSize;
      }
      if (left < rowLeft) {
        left = rowLeft;
      }

      // Constrain modal vertically within viewport boundary
      if (top < viewTop) {
        top = viewTop;
      }
      if (top + modalSize > viewBottom) {
        top = viewBottom - modalSize;
      }
      if (top < viewTop) {
        top = viewTop;
      }

      // Trailer takes width / 16 * 9 rounded up, Info takes the rest forming a 1:1 square
      const trailerHeight = Math.ceil((modalSize / 16) * 9);
      const infoHeight = modalSize - trailerHeight;

      setModalPos({
        left,
        top,
        width: modalSize,
        height: modalSize,
        trailerHeight,
        infoHeight
      });
    }
  };

  useEffect(() => {
    if (!hovered) return;

    let rafId = null;

    const handleScrollOrResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        updateModalPosition();
      });
    };

    window.addEventListener("scroll", handleScrollOrResize, { capture: true, passive: true });
    window.addEventListener("resize", handleScrollOrResize, { passive: true });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScrollOrResize, { capture: true });
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [hovered, projectInfo, thumbnailOrientation]);

  const scheduleCloseHover = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      if (!isOverCardRef.current && !isOverModalRef.current) {
        cancelTeaserRef.current = true;
        if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        if (teaserTimerRef.current) clearTimeout(teaserTimerRef.current);
        setHovered(false);
        setShowTeaserVideo(false);
        setIsTeaserMuted(true);
      }
    }, 150);
  };

  const handleCardMouseEnter = () => {
    if (disableHover || disableHoverPreview) {
      return;
    }
    isOverCardRef.current = true;
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);

    if (hovered) return;

    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    if (teaserTimerRef.current) clearTimeout(teaserTimerRef.current);
    cancelTeaserRef.current = false;
    setShowTeaserVideo(false);

    if (setActiveHoveredId) setActiveHoveredId(data?.show_id || data?.video_id);

    const triggerTeaserTimer = () => {
      teaserTimerRef.current = setTimeout(() => {
        if (!cancelTeaserRef.current) {
          setShowTeaserVideo(true);
        }
      }, 1000);
    };

    if (!trailerUrl) {
      resolveTeaserUrl(data, appInfo, pubid).then((resolvedUrl) => {
        if (!cancelTeaserRef.current && resolvedUrl) {
          setTrailerUrl(resolvedUrl);
        }
      });
    }

    triggerTeaserTimer();

    hoverTimeoutRef.current = setTimeout(() => {
      if (cardRef.current && !cancelTeaserRef.current && !disableHover && !disableHoverPreview) {
        updateModalPosition();
        setHovered(true);
      }
    }, 200);
  };

  const handleCardMouseLeave = () => {
    isOverCardRef.current = false;
    scheduleCloseHover();
  };

  const handleModalMouseEnter = () => {
    isOverModalRef.current = true;
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  };

  const handleModalMouseLeave = () => {
    isOverModalRef.current = false;
    scheduleCloseHover();
  };

  const handleCloseRemoveModal = () => {
    setIsRemoveModal(false);
    setHovered(false);
  };

  const ratioConfig = getThumbnailRatioConfig(projectInfo, thumbnailOrientation);

  return (
    <div
      ref={cardRef}
      className={`yowCardContainer showContents ${data?.type === "CONTINUE_WATCHING" ? "continue-watching-card" : ""} ${hovered ? "hovered" : ""} ${data?.type === "SMART_HOME" ? 'genre-card' : ''} ${rankIndex ? 'has-rank-number' : ''}`}
      title={data?.show_name || data?.video_title || data?.categoryname}
      tabIndex={typeof tabIndex !== 'undefined' ? tabIndex : 0}
      role={role || "group"}
      aria-label={ariaLabel || data?.show_name || data?.video_title || data?.categoryname}
      onKeyDown={mainCardKeyDown}
      onClick={handleCardClick}
      onMouseEnter={handleCardMouseEnter}
      onMouseLeave={handleCardMouseLeave}
    >
      {isRemoveModal &&
        ReactDOM.createPortal(
          <RemoveFromContinueWatchModal
            setIsRemoving={setIsRemoveModal}
            appInfo={appInfo}
            data={data}
            continueWatchingList={continueWatchingList}
            onRemove={onRemove}
            onCancel={handleCloseRemoveModal}
          />,
          document.body
        )
      }

      {data?.type === "CONTINUE_WATCHING" && hovered && (
        <div
          className="close-icon"
          tabIndex={0}
          role="button"
          aria-label="Close"
          onClick={(e) => { e.stopPropagation(); setIsRemoveModal(true); }}
          style={{ cursor: isRemoving ? "not-allowed" : "pointer", opacity: isRemoving ? 0.5 : 1 }}
          title={translateText('remove_from_continue_watching')}
        >
          <Closeicon />
        </div>
      )}

      {data?.type !== "SMART_HOME" && (
        <div className="premiumStatus">
          {data?.is_free_video === true ? (
            <div className="free">
              <img src={require("../../assets/Images/free_icon.png")} alt="FreeIcon" />
            </div>
          ) : data?.is_locked === true ? (
            <div className="lock">
              {pubid === 50158 ? (
                <img src={require("../../assets/Images/DocPlus_Plus.png")} className="docplus" alt="LockIcon" />
              ) : (
                <img src={require("../../assets/Images/lock.png")} alt="LockIcon" />
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Static poster card image container (No expansion on hover, fixed in row grid) */}
      <div className="imageContainer" style={{ aspectRatio: ratioConfig.cssValue }}>
        {rankIndex && (
          <span className={`yow-rank-number ${Number(rankIndex) >= 10 ? 'multi-digit' : ''}`}>
            {rankIndex}
          </span>
        )}
        <img
          className={`${thumbnailOrientation === "PORTRAIT" ? "portrait" : "landscape"} ${!isImageLoad ? "loading-img" : "showImage"}${data?.challenge_completed && isChallenge ? " blurred-image" : ""}`}
          src={imagURL}
          key={data?.show_id || data?.video_id}
          onError={(e) => replaceImage(e, thumbnailOrientation, projectInfo)}
          alt="ShowImage"
          loading={isPriority ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={isPriority ? "high" : "auto"}
        />
        {(data?.type === "CONTINUE_WATCHING" || (data?.watched_percentage > 0 && data?.watched_percentage < 100)) && (
          <div className="continueWatchingBar">
            <div className="line" style={{ width: data?.watched_percentage <= 1 ? 1 + "%" : data?.watched_percentage + "%" }}></div>
          </div>
        )}
        {data?.challenge_completed && isChallenge && (
          <div className="completionIndicator" title={translateText('already_watched')}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 13L9 17L20 8" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        )}
      </div>

      {metaData && (
        <div className="metaData">
          <h1 className="title">{data?.show_name || data?.video_title || data?.title || data?.categoryname}</h1>
        </div>
      )}

      {/* Floating 436x436 Square Hover Preview Popover Portal (Exact Figma Spec) */}
      {hovered && modalPos && ReactDOM.createPortal(
        <div
          className="yowHoverPreviewModal"
          style={{
            position: "fixed",
            left: `${modalPos.left}px`,
            top: `${modalPos.top}px`,
            width: `${modalPos.width}px`,
            height: `${modalPos.height}px`,
            "--trailer-height": modalPos.trailerHeight ? `${modalPos.trailerHeight}px` : "56.42%",
            "--info-height": modalPos.infoHeight ? `${modalPos.infoHeight}px` : "43.58%",
            zIndex: 9999999,
          }}
          onMouseEnter={handleModalMouseEnter}
          onMouseMove={handleModalMouseEnter}
          onMouseLeave={handleModalMouseLeave}
          onClick={navigateToDetailsPage}
        >
          {/* Top Half: 16:9 Trailer Video Section (shows landscape thumbnail for 1s, then plays teaser video) */}
          <div
            className="modalTrailerSection"
            style={{
              height: modalPos.trailerHeight ? `${modalPos.trailerHeight}px` : undefined,
              cursor: "pointer",
            }}
            onClick={navigateToDetailsPage}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigateToDetailsPage(e);
              }
            }}
            aria-label={`View details for ${data?.show_name || data?.video_title || "show"}`}
            title={`View details for ${data?.show_name || data?.video_title || "show"}`}
          >
            {showTeaserVideo && trailerUrl ? (
              <>
                <CardTrailerVideoPlayer
                  src={trailerUrl}
                  isMuted={isTeaserMuted}
                  onError={handleTeaserError}
                  onVideoClick={navigateToDetailsPage}
                />
                <button
                  className="cardTeaserMuteBtn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsTeaserMuted((prev) => !prev);
                  }}
                  aria-label={isTeaserMuted ? "Unmute sound" : "Mute sound"}
                  title={isTeaserMuted ? "Unmute sound" : "Mute sound"}
                >
                  {isTeaserMuted ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4.5 3.75L19.5 20.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M7.5 8.25V15.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M18 10.019C18.4825 10.5669 18.7487 11.2718 18.7487 12.0019C18.7487 12.7319 18.4825 13.4368 18 13.9847" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M14.25 14.475V21L7.5 15.75H3C2.80109 15.75 2.61032 15.671 2.46967 15.5303C2.32902 15.3897 2.25 15.1989 2.25 15V8.99999C2.25 8.80108 2.32902 8.61031 2.46967 8.46966C2.61032 8.32901 2.80109 8.24999 3 8.24999H7.5L8.13937 7.75311" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10.5142 5.90531L14.2501 3V10.0153" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M20.7817 7.5C21.8886 8.73755 22.5006 10.3397 22.5006 12C22.5006 13.6603 21.8886 15.2624 20.7817 16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14.25 3V21L7.5 15.75H3C2.80109 15.75 2.61032 15.671 2.46967 15.5303C2.32902 15.3897 2.25 15.1989 2.25 15V8.99999C2.25 8.80108 2.32902 8.61031 2.46967 8.46966C2.61032 8.32901 2.80109 8.24999 3 8.24999H7.5L14.25 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M18 10.019C18.4825 10.5669 18.7487 11.2718 18.7487 12.0019C18.7487 12.7319 18.4825 13.4368 18 13.9847" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M20.7817 7.5C21.8886 8.73755 22.5006 10.3397 22.5006 12C22.5006 13.6603 21.8886 15.2624 20.7817 16.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              </>
            ) : (
              <img
                src={
                  (data?.banner && imageUrlCheck(data.banner) ? data.banner : data?.banner ? `${process.env.REACT_APP_IMAGE_URL}${data.banner}` : null) ||
                  (data?.logo_thumb && imageUrlCheck(data.logo_thumb) ? data.logo_thumb : data?.logo_thumb ? `${process.env.REACT_APP_IMAGE_URL}${data.logo_thumb}` : null) ||
                  landscapeURL ||
                  imagURL
                }
                alt={data?.show_name || "Trailer Preview"}
                className="modalTrailerImg"
                onClick={navigateToDetailsPage}
                style={{ cursor: "pointer" }}
              />
            )}
          </div>

          {/* Bottom Half: Info Section (takes rest of the space, forming 1:1 square) */}
          <div className="modalInfoSection" style={{ height: modalPos.infoHeight ? `${modalPos.infoHeight}px` : undefined }}>
            <div className="modalHeaderRow">
              <h2 className="modalTitle">{data?.show_name || data?.video_title || data?.title || "A Month of Sundance..."}</h2>
              <div className="modalHeaderIcons">
                <button
                  className="modalIconButton"
                  onClick={handleWatchlistClick}
                  title={watchlistStatus === "added" ? "Remove from Watchlist" : "Add to Watchlist"}
                >
                  {watchlistStatus === "added" ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#FFC800" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="16 5 7 14 3 10" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M3.125 10H16.875" stroke="#8A8A88" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10 3.125V16.875" stroke="#8A8A88" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
                <button className="modalIconButton" onClick={(e) => { e.stopPropagation(); navigateToDetailsPage(e); }} title="More Details">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M13.75 11.875L17.5 8.125L13.75 4.375" stroke="#8A8A88" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M15 16.875H2.5V6.875" stroke="#8A8A88" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5.625 13.75C6.04084 12.1395 6.98028 10.7128 8.29554 9.69457C9.61081 8.6763 11.2273 8.12416 12.8906 8.125H17.5" stroke="#8A8A88" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>

            {(() => {
              const rawText = data?.synopsis || data?.description || data?.summary || data?.long_description || data?.short_description || data?.details || data?.story || data?.overview || data?.show_description || data?.video_description || "";
              const text = typeof rawText === "string" ? stripHtml(rawText).trim() : "";
              if (!text) return null;
              const cleanText = text.toLowerCase().startsWith("pause/rewind:") ? text.slice(13).trim() : text;
              return (
                <div className="modalPRInfo">
                  <strong className="yellowPRLabel">Pause/Rewind:</strong>{" "}{cleanText}
                </div>
              );
            })()}

            {(() => {
              const director = data?.director || data?.directors || data?.cast || data?.artist_name || data?.writer || data?.creator || "";
              const duration = data?.duration_text || data?.duration || data?.video_duration || "";
              if (!director && !duration) return null;
              return (
                <div className="modalDirectorInfo">
                  {director ? (
                    <>
                      <strong className="directorLabel">Directed by:</strong>{" "}{director}
                    </>
                  ) : (
                    duration
                  )}
                </div>
              );
            })()}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default YowCard;
